import React, { useEffect, useState } from 'react';

const steps = [
  { label: 'Payment received', detail: 'Confirming your transaction with Razorpay' },
  { label: 'Verifying payment', detail: 'Securely checking your payment signature' },
  { label: 'Granting access', detail: 'Sharing your notes on Google Drive' },
  { label: 'Almost done!', detail: 'Preparing your profile dashboard' },
];

const STEP_DURATION = 1800; // ms per step

export default function PaymentProcessingOverlay({ visible }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dots, setDots] = useState('');

  // Advance steps automatically while visible
  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      return;
    }
    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
    }, STEP_DURATION);
    return () => clearInterval(timer);
  }, [visible]);

  // Animated ellipsis
  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(timer);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(3, 7, 18, 0.97)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {/* Ambient glow blobs */}
      <div style={{
        position: 'absolute', width: 600, height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 480,
        margin: '0 16px',
        background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(9,9,20,0.98) 100%)',
        border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 24,
        padding: '48px 40px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
        textAlign: 'center',
      }}>
        {/* Top accent bar */}
        <div style={{
          position: 'absolute', top: 0, left: '10%', right: '10%', height: 2,
          borderRadius: '0 0 4px 4px',
          background: 'linear-gradient(90deg, transparent, #6366f1, #a855f7, transparent)',
        }} />

        {/* Spinner ring */}
        <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 32px' }}>
          <style>{`
            @keyframes rpm-spin { to { transform: rotate(360deg); } }
            @keyframes rpm-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.7; transform:scale(0.92); } }
            @keyframes rpm-fade-in { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
            @keyframes rpm-step-in { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
          `}</style>
          {/* Outer spinning ring */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: '#6366f1',
            borderRightColor: '#a855f7',
            animation: 'rpm-spin 0.9s linear infinite',
          }} />
          {/* Middle ring (counter-rotate) */}
          <div style={{
            position: 'absolute', inset: 8, borderRadius: '50%',
            border: '2px solid transparent',
            borderBottomColor: '#818cf8',
            animation: 'rpm-spin 1.4s linear infinite reverse',
          }} />
          {/* Center lock icon */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'rpm-pulse 2s ease-in-out infinite',
          }}>
            <svg width="28" height="28" fill="none" stroke="url(#shield-grad)" strokeWidth="1.8" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="shield-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#6366f1"/>
                  <stop offset="1" stopColor="#a855f7"/>
                </linearGradient>
              </defs>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M12 2l7 4v5c0 5.25-3.5 10.74-7 12C5.5 21.74 2 16.25 2 11V6l10-4z"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h2 style={{
          fontSize: 22, fontWeight: 700, color: '#f9fafb',
          margin: '0 0 8px',
          letterSpacing: '-0.3px',
        }}>
          Processing Your Payment{dots}
        </h2>
        <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 36px', lineHeight: 1.6 }}>
          {steps[currentStep].detail}
        </p>

        {/* Progress steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 36 }}>
          {steps.map((step, i) => {
            const isDone = i < currentStep;
            const isActive = i === currentStep;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                background: isActive
                  ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(168,85,247,0.10))'
                  : isDone ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
                border: isActive
                  ? '1px solid rgba(99,102,241,0.35)'
                  : isDone ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(255,255,255,0.05)',
                transition: 'all 0.4s ease',
                animation: isActive ? 'rpm-step-in 0.35s ease both' : 'none',
              }}>
                {/* Icon */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isDone
                    ? 'rgba(16,185,129,0.15)'
                    : isActive
                      ? 'rgba(99,102,241,0.2)'
                      : 'rgba(255,255,255,0.05)',
                  border: isDone
                    ? '1px solid rgba(16,185,129,0.4)'
                    : isActive
                      ? '1px solid rgba(99,102,241,0.4)'
                      : '1px solid rgba(255,255,255,0.08)',
                }}>
                  {isDone ? (
                    <svg width="13" height="13" fill="none" stroke="#10b981" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  ) : isActive ? (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                      animation: 'rpm-pulse 1s ease-in-out infinite',
                    }} />
                  ) : (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(156,163,175,0.4)' }} />
                  )}
                </div>
                {/* Label */}
                <span style={{
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  color: isDone ? '#6ee7b7' : isActive ? '#e0e7ff' : '#6b7280',
                  transition: 'color 0.3s',
                }}>
                  {step.label}
                </span>
                {/* "Processing" badge */}
                {isActive && (
                  <span style={{
                    marginLeft: 'auto', fontSize: 10, fontWeight: 600,
                    color: '#818cf8', letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>
                    processing
                  </span>
                )}
                {isDone && (
                  <span style={{
                    marginLeft: 'auto', fontSize: 10, fontWeight: 600,
                    color: '#34d399', letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>
                    done
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Warning box */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px',
          borderRadius: 12,
          background: 'rgba(251,191,36,0.06)',
          border: '1px solid rgba(251,191,36,0.25)',
        }}>
          <svg width="18" height="18" style={{ flexShrink: 0, marginTop: 1 }}
            fill="none" stroke="#fbbf24" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          </svg>
          <p style={{ fontSize: 12, color: '#fde68a', margin: 0, lineHeight: 1.6, textAlign: 'left' }}>
            <strong>Do not close this window or navigate away.</strong><br/>
            Your access is being set up. This usually takes a few seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
